import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { notifyIcon } from "../../../config/constants";
import { getUserProfileControlsData } from "../../../shared/services/auth.service";
import { getUserChips } from "../../../shared/services/workflow.services";
import { getNotification } from "../../../utils/common";
import { ExpressionBuilderModal, useExpressionBuilder } from "../ExpressionBuilder";
import { getWorkflowNodeExpression } from "../ExpressionBuilder/services/expressionBuilder.service";
import { useWorkflowContext } from "../WorkflowContextProvider";

const SwitchNodePopup = ({ onClose, nodeId }) => {
  const { workflowCustomVariables, originalWorkflowVariables } = useSelector((state) => state.taskSidebar);
  const { workflowId } = useParams();
  const { user } = useSelector((state) => state.auth);
  const [nodeExpression, setNodeExpression] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userChips, setUserChips] = useState([]);
  const {taskId} = useSelector((state) => state.taskSidebar.task);
  const { workflowDefaults } = useWorkflowContext();
  const isTaskSwitchNode = taskId ;

  /**
   * Fetches both user chips and node expression simultaneously
   * @author Abhishek Dhillon
   */
  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);

      // Use Promise.all for parallel execution
      const [userChipsResponse, nodeExpressionResponse] = await Promise.all([
        getUserProfileControlsData({ companyId: user?.companyId, drutasUserId: user?.id }),
        getWorkflowNodeExpression(isTaskSwitchNode ? { taskNodeExpressionId: nodeId } : { id: nodeId }),
      ]);
      // Handle user chips response
      if (userChipsResponse) {
        setUserChips(userChipsResponse?.WorkInformations);
      }
      // Handle node expression response
      if (nodeExpressionResponse?.expression) {
        setNodeExpression(nodeExpressionResponse.expression);
      } else {
        // If no expression found, start with empty expression
        setNodeExpression(null);
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
      getNotification("Failed to load data", notifyIcon.ERROR_ICON);
      setNodeExpression(null);
    } finally {
      setIsLoading(false);
    }
  }, [user?.companyId, nodeId, isTaskSwitchNode]);

  // Load initial data on mount
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Use expression builder hook for state management (only after loading is complete)
  const {
    expression,
    updateRootExpression,
    handleSaveExpression,
    handleTestExpression,
    testData,
    evaluationResult,
    availableFields,
    fieldValidators,
    isSubmitting,
    submitError,
  } = useExpressionBuilder(workflowCustomVariables, onClose, nodeId, nodeExpression, userChips, workflowDefaults?.adminCustomVariables);

  // Show loading state while fetching expression
  if (isLoading) {
    return (
      <div className='expression-loading-container'>
        <div className='d-flex justify-content-center align-items-center p-4'>
          <div className='spinner-border text-primary' role='status'>
            <span className='sr-only'>Loading expression...</span>
          </div>
          <span className='ms-2'>Loading saved expression...</span>
        </div>
      </div>
    );
  }

  return (
    <ExpressionBuilderModal
      onClose={onClose}
      expression={expression}
      updateRootExpression={updateRootExpression}
      handleSaveExpression={handleSaveExpression}
      handleTestExpression={handleTestExpression}
      availableFields={availableFields}
      testData={testData}
      evaluationResult={evaluationResult}
      fieldValidators={fieldValidators}
      isSubmitting={isSubmitting}
      submitError={submitError}
    />
  );
};

export default SwitchNodePopup;
